import { waitForTransition } from './transition';

export const COLLAPSE_EVENTS = Object.freeze({
  SHOW: 'ui:collapse:show',
  SHOWN: 'ui:collapse:shown',
  HIDE: 'ui:collapse:hide',
  HIDDEN: 'ui:collapse:hidden',
});

const instances = new WeakMap();

function dispatch(element, name, trigger) {
  return element.dispatchEvent(
    new CustomEvent(name, {
      bubbles: true,
      cancelable: name === COLLAPSE_EVENTS.SHOW || name === COLLAPSE_EVENTS.HIDE,
      detail: { trigger },
    })
  );
}

export function getTargetSelector(trigger) {
  const uiTarget = trigger.getAttribute('data-ui-target');
  const href = trigger.getAttribute('href');
  const selector = uiTarget || (href?.startsWith('#') ? href : null);

  return selector && selector !== '#' ? selector : null;
}

function resolveTarget(trigger) {
  const selector = getTargetSelector(trigger);

  if (!selector) {
    return null;
  }

  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

export class Collapse {
  constructor(element) {
    this.element = element;
    this.triggers = new Set();
    this.state = element.dataset.state === 'open' ? 'open' : 'closed';
    this.element.dataset.uiCollapse = '';
    this.element.dataset.state = this.state;
  }

  addTrigger(trigger) {
    this.triggers.add(trigger);

    if (this.element.id && !trigger.hasAttribute('aria-controls')) {
      trigger.setAttribute('aria-controls', this.element.id);
    }

    this.syncTrigger(trigger, this.state === 'open');
  }

  isOpen() {
    return this.state === 'opening' || this.state === 'open';
  }

  async show(trigger = null) {
    if (this.state !== 'closed') {
      return false;
    }

    if (!dispatch(this.element, COLLAPSE_EVENTS.SHOW, trigger)) {
      return false;
    }

    this.state = 'opening';
    this.element.dataset.state = this.state;
    this.syncTriggers(true);

    this.element.style.height = '0px';
    this.element.offsetHeight;
    this.element.style.height = `${this.element.scrollHeight}px`;

    await waitForTransition(this.element);

    this.element.style.height = '';
    this.state = 'open';
    this.element.dataset.state = this.state;
    dispatch(this.element, COLLAPSE_EVENTS.SHOWN, trigger);

    return true;
  }

  async hide(trigger = null) {
    if (this.state !== 'open') {
      return false;
    }

    if (!dispatch(this.element, COLLAPSE_EVENTS.HIDE, trigger)) {
      return false;
    }

    this.state = 'closing';
    this.element.dataset.state = this.state;
    this.syncTriggers(false);

    this.element.style.height = `${this.element.getBoundingClientRect().height}px`;
    this.element.offsetHeight;
    this.element.style.height = '0px';

    await waitForTransition(this.element);

    this.element.style.height = '';
    this.state = 'closed';
    this.element.dataset.state = this.state;
    dispatch(this.element, COLLAPSE_EVENTS.HIDDEN, trigger);

    return true;
  }

  toggle(trigger = null) {
    return this.isOpen() ? this.hide(trigger) : this.show(trigger);
  }

  syncTrigger(trigger, isOpen) {
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    trigger.dataset.state = isOpen ? 'open' : 'closed';
  }

  syncTriggers(isOpen) {
    this.triggers.forEach((trigger) => {
      this.syncTrigger(trigger, isOpen);
    });
  }
}

export function getCollapse(trigger) {
  const target = resolveTarget(trigger);
  if (!target) {
    return null;
  }

  if (!instances.has(target)) {
    instances.set(target, new Collapse(target));
  }

  const collapse = instances.get(target);
  collapse.addTrigger(trigger);
  return collapse;
}
